import type { PodcastEpisode, WithContext } from 'schema-dts';
import type { Contributor } from 'types/cms/blog/Contributors';
import type { Episode } from 'types/cms/blog/Episode';
import { getPageUrl } from 'utils/getBaseUrl';

import { orgId, podcastEpisodeId } from './helpers/ids';

interface BuildPodcastEpisodeInput {
  // Episode is required at this layer. Callers must narrow BlogPost.episode
  // (which is nullable) before invoking - matching how the surrounding UI
  // already guards podcast-only fields.
  episode: Episode;
  summary: string;
  publishedAt: string;
  contributors: Contributor[] | null;
  pagePath: string;
}

export const buildPodcastEpisode = ({
  episode,
  summary,
  publishedAt,
  contributors,
  pagePath,
}: BuildPodcastEpisodeInput): WithContext<PodcastEpisode> => ({
  '@context': 'https://schema.org',
  '@type': 'PodcastEpisode',
  '@id': podcastEpisodeId(pagePath),
  name: episode.episodeTitle,
  url: getPageUrl(pagePath),
  description: summary,
  datePublished: publishedAt,
  ...(episode.thumbnail ? { image: episode.thumbnail } : {}),
  associatedMedia: {
    '@type': 'AudioObject',
    contentUrl: episode.audio,
  },
  partOfSeries: {
    '@type': 'PodcastSeries',
    name: episode.podcastTitle,
  },
  author:
    contributors && contributors.length > 0
      ? contributors.map((c) => ({
          '@type': 'Person' as const,
          name: c.contributor.name,
        }))
      : [{ '@type': 'Organization' as const, '@id': orgId() }],
});
